import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Platform, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader, colors, spacing, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, BillingOverview, BillingCatalog } from '../services/api';
import { startCheckout, canPayInApp } from '../services/checkout';
import { openInvoice } from '../services/invoices';
import { handleApiError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';

const formatMoney = (amount: number, currency: string, locale: string) => {
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-US', { style: 'currency', currency, maximumFractionDigits: amount % 1 ? 2 : 0 }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const formatDate = (iso: string | undefined, locale: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

/** Billing: current plan and status, renew (manual renewal), change plan,
 * payment history. Plan changes and payments are owner/admin only (the
 * server enforces it too). */
export const BillingScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const copyValue = async (value: string) => {
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) await navigator.clipboard.writeText(value);
      else await Clipboard.setStringAsync(value);
      showNotification('success', t('profile.copy.successTitle'), value);
    } catch {
      showNotification('error', t('profile.copy.errorTitle'), t('profile.copy.errorMessage'));
    }
  };
  const { t, locale } = useLocale();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [busyInvoice, setBusyInvoice] = useState<string | null>(null);

  const onViewInvoice = async (paymentId: string) => {
    setBusyInvoice(paymentId);
    try {
      await openInvoice(paymentId);
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setBusyInvoice(null);
    }
  };

  const onEmailInvoice = async (paymentId: string) => {
    setBusyInvoice(paymentId);
    try {
      const res = await apiService.emailInvoice(paymentId);
      if (res.statusCode === 1000) showNotification('success', t('billing.invoiceEmailedTitle'), t('billing.invoiceEmailedBody'));
      else showNotification('error', t('billing.errorTitle'), res.errors?.[0]?.message || t('billing.errorGeneric'));
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setBusyInvoice(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [res, cat] = await Promise.all([apiService.getBillingOverview(), apiService.getBillingCatalog().catch(() => null)]);
      if (res.statusCode === 1000 && res.data) setOverview(res.data);
      if (cat?.statusCode === 1000 && cat.data) setCatalog(cat.data);
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const plan = overview?.plan;
  const sub = overview?.subscription;
  const isPaidPlan = !!plan && !plan.isFree;
  const cycle = sub?.billingCycle || 'monthly';
  const renewPrice = plan ? (cycle === 'yearly' ? plan.yearPrice : cycle === 'quarterly' ? plan.quartPrice : plan.moPrice) : 0;
  const showRenew = !!overview?.canManage && isPaidPlan && renewPrice > 0 && canPayInApp() && overview.paymentsEnabled;

  const onRenew = async () => {
    if (!plan) return;
    setPaying(true);
    try {
      const checkout = await startCheckout(plan._id, cycle, locale === 'ar' ? 'ar' : 'en');
      if (checkout) navigateTo('billing-return', undefined, { paymentId: checkout.paymentId, from: 'billing' });
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setPaying(false);
    }
  };

  const statusChip = () => {
    if (!overview) return null;
    const map: Record<string, { label: string; color: string }> = {
      active: { label: t('billing.status.active'), color: colors.status.success as string },
      free: { label: t('billing.status.free'), color: colors.status.info as string },
      trial: { label: t('billing.status.trial'), color: colors.status.warning as string },
      grace: { label: t('billing.status.grace'), color: colors.status.warning as string },
      expired: { label: t('billing.status.expired'), color: colors.status.error as string },
      none: { label: t('billing.status.none'), color: colors.textLight as string },
    };
    const s = map[overview.state];
    return (
      <View style={[styles.chip, { backgroundColor: withAlpha(s.color, 0.12), borderColor: withAlpha(s.color, 0.4) }]}>
        <Text style={[styles.chipText, { color: s.color }]}>{s.label}</Text>
      </View>
    );
  };

  const periodLine = () => {
    if (!overview || !sub) return null;
    const end = formatDate(sub.currentPeriodEnd, locale);
    if (overview.state === 'trial') return t('billing.trialEnds', { date: end, days: overview.daysLeft });
    if (overview.state === 'free') return t('billing.freeForever');
    if (overview.state === 'active') return t('billing.paidUntil', { date: end, days: overview.daysLeft });
    if (overview.state === 'grace') return t('billing.graceLine', { date: end, lockDate: formatDate(overview.lockAt || undefined, locale) });
    if (overview.state === 'expired') return t('billing.endedOn', { date: end });
    return null;
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={t('billing.title')} onBack={() => navigateTo('settings')} />}
    >
      {loading ? (
        <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
      ) : (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.content}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} />}
        >
          {overview?.state === 'grace' && (
            <View style={[styles.graceBanner, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="time" size={18} color={colors.status.warning as string} />
              <Text style={[styles.lockText, { textAlign: rtl.textStart }]}>
                {t('billing.graceBanner', { days: overview.graceDaysLeft || 0, date: formatDate(overview.lockAt || undefined, locale) })}
              </Text>
            </View>
          )}

          {overview?.businessLocked && (
            <View style={[styles.lockBanner, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="lock-closed" size={18} color={colors.status.error as string} />
              <Text style={[styles.lockText, { textAlign: rtl.textStart }]}>
                {canPayInApp() ? t('billing.lockedBody') : t('billing.lockedBodyNoPay')}
              </Text>
            </View>
          )}

          <View style={styles.card}>
            <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.planName, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {plan?.name || t('billing.noPlan')}
              </Text>
              {statusChip()}
            </View>
            {!!periodLine() && <Text style={[styles.periodLine, { textAlign: rtl.textStart }]}>{periodLine()}</Text>}
            {isPaidPlan && renewPrice > 0 && (
              <Text style={[styles.priceLine, { textAlign: rtl.textStart }]}>
                {formatMoney(renewPrice, plan!.currency, locale)} / {t(`billing.per.${cycle}` as any)}
              </Text>
            )}

            {overview?.canManage ? (
              <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                {showRenew && (
                  <TouchableOpacity style={[styles.primaryButton, paying && styles.disabled]} onPress={onRenew} disabled={paying}>
                    {paying ? <ActivityIndicator color={colors.literal.white as string} /> : (
                      <Text style={styles.primaryButtonText}>
                        {overview.state === 'trial' || overview.state === 'expired' || overview.state === 'grace' ? t('billing.payNow') : t('billing.renew')}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={styles.secondaryButton} onPress={() => navigateTo('plan-selection', undefined, { mode: 'change' })}>
                  <Text style={styles.secondaryButtonText}>{overview.state === 'none' ? t('billing.choosePlan') : t('billing.changePlan')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <Text style={[styles.mutedNote, { textAlign: rtl.textStart }]}>{t('billing.ownerOnly')}</Text>
            )}
            {(overview?.businessLocked || !overview?.canManage) && (
              // Billing is per business: a paused business (or one you can't
              // pay for) doesn't stop you working in your other businesses.
              <TouchableOpacity style={[styles.switchLink, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('business-picker')}>
                <Ionicons name="swap-horizontal" size={16} color={colors.primary as string} />
                <Text style={styles.switchText}>{t('billing.switchBusiness')}</Text>
              </TouchableOpacity>
            )}
            {overview?.canManage && isPaidPlan && overview.state === 'active' && (
              <Text style={[styles.mutedNote, { textAlign: rtl.textStart }]}>{t('billing.manualRenewalNote')}</Text>
            )}
          </View>

          {!!overview && !overview.plan?.isFree && overview.state !== 'none' && (
            <View style={styles.card}>
              <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.planName, { textAlign: rtl.textStart }]}>{t('billing.addons.title')}</Text>
                <TouchableOpacity onPress={() => navigateTo('billing-addons')} hitSlop={6}>
                  <Text style={styles.secondaryButtonText}>
                    {overview.addons?.length ? t('billing.addons.manage') : t('billing.addons.addMore')}
                  </Text>
                </TouchableOpacity>
              </View>
              <Text style={[styles.mutedNote, { textAlign: rtl.textStart }]}>
                {overview.addons?.length
                  ? overview.addons.map((a) => {
                      const def = catalog?.addons.find((d) => d.code === a.code);
                      const name = def?.translations?.[locale]?.name || def?.name || a.code;
                      return def?.kind === 'module' ? name : `${name} × ${a.quantity}`;
                    }).join(' · ')
                  : t('billing.addons.pitch')}
              </Text>
            </View>
          )}

          {!!overview?.payments?.length && (
            <>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('billing.history')}</Text>
              <View style={styles.card}>
                {overview.payments.map((p, index) => {
                  const ok = p.status === 'completed';
                  return (
                    <View key={p._id} style={[styles.paymentRow, { flexDirection: rtl.rowDirection }, index > 0 && styles.rowDivider]}>
                      <Ionicons
                        name={ok ? 'checkmark-circle' : p.status === 'refunded' ? 'return-down-back' : 'close-circle'}
                        size={18}
                        color={(ok ? colors.status.success : p.status === 'refunded' ? colors.status.info : colors.status.error) as string}
                      />
                      <View style={styles.paymentBody}>
                        <Text style={[styles.paymentAmount, { textAlign: rtl.textStart }]}>{formatMoney(p.amount, p.currency, locale)}</Text>
                        <Text style={[styles.paymentMeta, { textAlign: rtl.textStart }]} numberOfLines={1}>
                          {formatDate(p.paidAt || p.createdAt, locale)}
                          {p.billingCycle ? ` · ${t(`billing.cycle.${p.billingCycle}` as any)}` : ''}
                        </Text>
                        {/* Payment ref = Kashier's order number / paymentId; invoice no. once paid. */}
                        {[
                          p.reference ? { label: t('billing.paymentRef'), value: p.reference } : null,
                          p.invoiceNumber ? { label: t('billing.invoiceNo'), value: p.invoiceNumber } : null,
                        ].filter(Boolean).map((ref: any) => (
                          <TouchableOpacity
                            key={ref.label}
                            onPress={() => copyValue(ref.value)}
                            hitSlop={6}
                            style={[styles.refRow, { flexDirection: rtl.rowDirection }]}
                            accessibilityRole="button"
                            accessibilityLabel={`${t('common.copy')} ${ref.value}`}
                          >
                            <Text style={styles.paymentMeta} numberOfLines={1}>{ref.label}</Text>
                            <Text style={styles.refValue} numberOfLines={1} selectable>{ref.value}</Text>
                            <Ionicons name="copy-outline" size={13} color={colors.textLight as string} />
                          </TouchableOpacity>
                        ))}
                        {ok && (
                          <View style={[styles.invoiceActions, { flexDirection: rtl.rowDirection }]}>
                            {busyInvoice === p._id ? <ActivityIndicator size="small" color={colors.primary as string} /> : (
                              <>
                                <TouchableOpacity onPress={() => onViewInvoice(p._id)} hitSlop={6} style={[styles.invoiceAction, { flexDirection: rtl.rowDirection }]}>
                                  <Ionicons name="document-text-outline" size={14} color={colors.primary as string} />
                                  <Text style={styles.invoiceActionText}>{t('billing.viewInvoice')}</Text>
                                </TouchableOpacity>
                                {overview.canManage && (
                                  <TouchableOpacity onPress={() => onEmailInvoice(p._id)} hitSlop={6} style={[styles.invoiceAction, { flexDirection: rtl.rowDirection }]}>
                                    <Ionicons name="mail-outline" size={14} color={colors.primary as string} />
                                    <Text style={styles.invoiceActionText}>{t('billing.emailInvoice')}</Text>
                                  </TouchableOpacity>
                                )}
                              </>
                            )}
                          </View>
                        )}
                      </View>
                      <Text style={[styles.paymentStatus, { color: (ok ? colors.status.success : colors.textLight) as string }]}>
                        {t(`billing.paymentStatus.${p.status}` as any)}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </>
          )}
        </ScrollView>
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1 },
  content: { padding: spacing.md, paddingBottom: spacing.xl * 2, gap: spacing.md, maxWidth: 760, width: '100%', alignSelf: 'center' },
  lockBanner: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: withAlpha(colors.status.error, 0.08),
    borderWidth: 1,
    borderColor: withAlpha(colors.status.error, 0.3),
  },
  graceBanner: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: withAlpha(colors.status.warning, 0.1),
    borderWidth: 1,
    borderColor: withAlpha(colors.status.warning, 0.35),
  },
  switchLink: { alignItems: 'center', gap: 6, marginTop: spacing.sm, alignSelf: 'flex-start' },
  switchText: { color: colors.primary, fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.sm },
  lockText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.xs,
  },
  cardHeader: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  planName: { flex: 1, fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.textDark },
  chip: { borderWidth: 1, borderRadius: 12, paddingHorizontal: spacing.sm, paddingVertical: 2 },
  chipText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold },
  periodLine: { fontSize: typography.fontSize.sm, color: colors.textDark },
  priceLine: { fontSize: typography.fontSize.sm, color: colors.textLight },
  actions: { gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap' },
  primaryButton: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: 10, paddingHorizontal: spacing.lg, minWidth: 120, alignItems: 'center' },
  primaryButtonText: { color: colors.literal.white, fontFamily: typography.fontFamily.semibold },
  secondaryButton: { borderWidth: 1, borderColor: colors.primary, borderRadius: 10, paddingVertical: 10, paddingHorizontal: spacing.lg, alignItems: 'center' },
  secondaryButtonText: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  disabled: { opacity: 0.6 },
  mutedNote: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: spacing.xs },
  sectionTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  paymentRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  rowDivider: { borderTopWidth: 1, borderTopColor: colors.border },
  paymentBody: { flex: 1, minWidth: 0 },
  paymentAmount: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  paymentMeta: { fontSize: typography.fontSize.xs, color: colors.textLight },
  refRow: { alignItems: 'center', gap: 6, marginTop: 2, alignSelf: 'flex-start' },
  refValue: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.semibold, ...(Platform.OS === 'web' ? { fontVariant: ['tabular-nums'] as any } : {}) },
  invoiceActions: { gap: spacing.md, marginTop: 4, alignItems: 'center' },
  invoiceAction: { alignItems: 'center', gap: 4 },
  invoiceActionText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  paymentStatus: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold },
});
